<script setup lang="tsx">
import { computed, onMounted, ref, watch } from 'vue'

import Modal from '../shared/Modal.vue'
import http from '@/services/http'
import toastStore from '@/stores/toast'
import type { Response, SecurityMethod, SecurityMethodResponse } from '@/models/api-generated'
import { High, Main, Standard, VEITS } from '@/models/security-method-enums-generated'
import { useI18n } from 'vue-i18n'
import { categories, securityNeed } from '@/constants/method'
import { hasPermission } from '@/services/utils'
import { PermAdminSecurityNeed } from '@/models/enums-generated'
import { getTypeIcon, viewing } from '@/services/utils'

const model = ref<SecurityMethodResponse>()
const isDialogVisible = ref(false)
const toaster = toastStore()
const { t } = useI18n()
var isVeitsSupported = ref(false)

let orig: string

watch(
	() => model.value?.overwriteSecurityMethod.type,
	(newType) => {
		if (!model.value) return
		if (newType !== High) {
			model.value.overwriteSecurityMethod.confidentiality = false
			model.value.overwriteSecurityMethod.integrity = false
			model.value.overwriteSecurityMethod.availability = false
		}
		const bpType = model.value.businessProcessSecurityMethod.type

		if (newType === VEITS) {
			model.value.overwriteSecurityMethod.type = VEITS
			return
		}

		if ((bpType === High && newType !== High) || (bpType === Standard && newType === Main)) {
			model.value.overwriteSecurityMethod.type = bpType
		}
	},
	{ immediate: true },
)

async function fetch() {
	const { data } = await http.get<Response<SecurityMethodResponse>>('api/security-method/get')

	if (!data.data) return
	model.value = data.data

	const { data: veitsResp } = await http.get<Response<boolean>>('api/isVeitsSupported')
	if (veitsResp.status === 'OK' && veitsResp.data !== undefined) {
		isVeitsSupported.value = veitsResp.data
	}
}

async function setMethod() {
	if (!model.value) return
	const { data } = await http.post<Response<SecurityMethod>>('api/security-method/set', model.value.overwriteSecurityMethod)

	if (data.status === 'OK') {
		isDialogVisible.value = false
		toaster.showSuccess(t('securityMethodChangedSuccessfully'))
	}
}

function openDialog() {
	if (!isValid.value) return

	isDialogVisible.value = true
}

const canEdit = computed(() => hasPermission(PermAdminSecurityNeed))
const isValid = computed(() => {
	if (!model.value) return
	if (model.value.overwriteSecurityMethod.type === High) {
		return [
			model.value.overwriteSecurityMethod.confidentiality,
			model.value.overwriteSecurityMethod.integrity,
			model.value.overwriteSecurityMethod.availability,
		].some((c) => c)
	}
	return true
})

const isOptionBlocked = (option: string) => {
	if (!model.value) return false

	const bpType = model.value.businessProcessSecurityMethod.type
	if (option === VEITS && !isVeitsSupported.value) {
		return true
	}
	if (bpType === Standard) {
		return option === Main
	}
	if (bpType === High) {
		return option === Standard || option === Main
	}

	return false
}

onMounted(() => {
	fetch()
})
</script>

<template>
	<template v-if="viewing > 0 && model">
		<template v-if="canEdit">
			<div class="rounded-container p-4 mb-2">
				<div class="mb-3 fw-bold">
					<label for="security-method" class="form-label">
						{{ t('securityNeed.overwrite') }}
					</label>
					<select v-model="model.overwriteSecurityMethod.type" id="security-method" class="form-select">
						<option v-for="option in securityNeed" :key="option" :value="option" :disabled="isOptionBlocked(option)">
							{{ t(`securityNeed.${option}`) }}
						</option>
					</select>
				</div>

				<div v-if="model.overwriteSecurityMethod.type === High" class="mb-3">
					<div class="mb-2">{{ t('securityNeed.error.selectAtLeastOne') }}:</div>

					<div v-for="field in categories" :key="field" class="form-check">
						<input
							class="form-check-input"
							type="checkbox"
							:id="field"
							v-model="model.overwriteSecurityMethod[field]"
							:true-value="true"
							:false-value="false"
						/>
						<label class="form-check-label" :for="field">
							{{ t(field) }}
						</label>
					</div>
				</div>

				<div class="d-grid gap-2">
					<button class="btn btn-primary" type="button" @click="openDialog" :disabled="!isValid">
						{{ t('misc.confirm') }}
					</button>
				</div>
			</div>

			<Modal v-model="isDialogVisible" @confirm="setMethod" @cancel="model.overwriteSecurityMethod.type = orig">
				<template #header>
					<h5>{{ t('securityNeed.dialog.title') }}</h5>
				</template>
				<template #body>
					<p>{{ t('securityNeed.dialog.body1') }}</p>
					<p>{{ t('securityNeed.dialog.body2') }}</p>
				</template>
			</Modal>
		</template>
		<div class="rounded-container p-4">
			<div class="mb-3 fw-bold">
				{{ t('securityNeed.bpTitle') }}
			</div>
			<div class="mb-3">
				<div class="icon-container">
					<i :class="getTypeIcon(model.businessProcessSecurityMethod.type)"></i>
					{{ t(`securityNeed.${model.businessProcessSecurityMethod.type}`) }}
				</div>
			</div>
			<div v-if="model.businessProcessSecurityMethod.type === High" class="mb-3">
				<div class="mb-2">
					{{ t('securityNeed.category') }}:
					<span>
						{{ model.businessProcessSecurityMethod.confidentiality ? 'C' : '-' }}
						{{ model.businessProcessSecurityMethod.integrity ? 'I' : '-' }}
						{{ model.businessProcessSecurityMethod.availability ? 'A' : '-' }}
					</span>
				</div>
			</div>
		</div>
	</template>
</template>
